import React, { useEffect, useMemo, useRef, useState } from "react";
import { assetUrl } from "../../api/http";
import { getContainedImageBounds } from "../../utils/pendantPreview";

type EngravingPreviewArea = {
  side?: "front" | "back";
  xPct?: number;
  yPct?: number;
  widthPct?: number;
  heightPct?: number;
  rotation?: number;
  enabled?: boolean;
  maxLines?: number;
  textLimit?: number;
};

type EngravingPreviewSide = {
  enabled?: boolean;
  text?: string;
  sanitizedText?: string;
  lines?: string[];
  fontKey?: string;
  fontFamily?: string;
  fontSize?: number;
  fittedFontSize?: number;
  referenceWidthPx?: number;
  referenceHeightPx?: number;
  lineHeight?: number;
  letterSpacingPx?: number;
  normalizedFontSizePct?: number;
  normalizedLetterSpacingEm?: number;
  area?: EngravingPreviewArea;
  textLimit?: number;
  maxLines?: number;
  contentPaddingPct?: number;
  contentOffsetXPct?: number;
  contentOffsetYPct?: number;
  contentScale?: number;
};

export type EngravingPreviewSelection = {
  activeSide?: "front" | "back";
  front: EngravingPreviewSide;
  back?: EngravingPreviewSide;
  preview?: {
    frontImage?: string;
    backImage?: string;
  };
};

export type EngravingPreviewLayout = {
  side: "front" | "back";
  renderedFontSizePx: number;
  fittedFontSize: number;
  lineCount: number;
  contentWidthPx: number;
  contentHeightPx: number;
};

type Props = {
  selection: EngravingPreviewSelection | null | undefined;
  mode?: "single" | "split" | "auto";
  activeSide?: "front" | "back";
  showSideLabels?: boolean;
  onSideLayout?: (layout: EngravingPreviewLayout) => void;
};

const DEFAULT_FONT_SIZE = 18;
const LAYOUT_REFERENCE_BOX_PX = 1000;
const FIXED_TEXT_COLOR = "rgba(17, 24, 39, 0.88)";
const CONTENT_PADDING_PCT = 4;
const MIN_CONTENT_SCALE = 0.6;
const MAX_CONTENT_SCALE = 1.6;
const MAX_CONTENT_OFFSET_PCT = 40;

const FONT_OPTIONS = {
  serif: {
    family: 'Georgia, "Times New Roman", serif',
    widthFactor: 0.57,
    lineHeight: 1.14,
    letterSpacingPx: 0.15,
  },
  sans: {
    family: '"Trebuchet MS", "Helvetica Neue", Arial, sans-serif',
    widthFactor: 0.55,
    lineHeight: 1.1,
    letterSpacingPx: 0.1,
  },
  script: {
    family: '"Brush Script MT", "Segoe Script", "Snell Roundhand", cursive',
    widthFactor: 0.64,
    lineHeight: 1.08,
    letterSpacingPx: 0,
  },
  handwritten: {
    family: '"Comic Sans MS", "Bradley Hand", "Marker Felt", cursive',
    widthFactor: 0.6,
    lineHeight: 1.08,
    letterSpacingPx: 0.05,
  },
  "bold-classic": {
    family: '"Arial Black", "Palatino Linotype", "Book Antiqua", serif',
    widthFactor: 0.62,
    lineHeight: 1.12,
    letterSpacingPx: 0.2,
  },
} as const;

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

function getFontConfig(fontKey?: string) {
  const normalized = String(fontKey || "").trim().toLowerCase() as keyof typeof FONT_OPTIONS;
  return FONT_OPTIONS[normalized] || FONT_OPTIONS.serif;
}

function normalizeArea(area?: EngravingPreviewArea | null, side: "front" | "back" = "front") {
  const widthPct = clamp(Number(area?.widthPct ?? 70), 1, 100);
  const heightPct = clamp(Number(area?.heightPct ?? 22), 1, 100);
  return {
    side,
    enabled: area?.enabled ?? true,
    xPct: clamp(Number(area?.xPct ?? 15), 0, 100 - widthPct),
    yPct: clamp(Number(area?.yPct ?? 30), 0, 100 - heightPct),
    widthPct,
    heightPct,
    rotation: Number.isFinite(Number(area?.rotation)) ? Number(area?.rotation) : 0,
    maxLines:
      Number.isFinite(Number(area?.maxLines)) && Number(area?.maxLines) > 0
        ? Math.round(Number(area?.maxLines))
        : undefined,
    textLimit:
      Number.isFinite(Number(area?.textLimit)) && Number(area?.textLimit) >= 0
        ? Math.round(Number(area?.textLimit))
        : undefined,
  };
}

function resolveDisplayArea(
  area: EngravingPreviewArea | undefined,
  imageBounds?: {
    xPct: number;
    yPct: number;
    widthPct: number;
    heightPct: number;
  } | null
) {
  const normalizedArea = normalizeArea(area);
  if (!imageBounds) {
    return normalizedArea;
  }
  return {
    ...normalizedArea,
    xPct: imageBounds.xPct + (normalizedArea.xPct / 100) * imageBounds.widthPct,
    yPct: imageBounds.yPct + (normalizedArea.yPct / 100) * imageBounds.heightPct,
    widthPct: (normalizedArea.widthPct / 100) * imageBounds.widthPct,
    heightPct: (normalizedArea.heightPct / 100) * imageBounds.heightPct,
  };
}

function charLength(value: string) {
  return Array.from(value).length;
}

function breakLongToken(token: string, maxCharsPerLine: number) {
  const chars = Array.from(token);
  if (chars.length <= maxCharsPerLine) return [token];
  const chunks: string[] = [];
  for (let index = 0; index < chars.length; index += maxCharsPerLine) {
    chunks.push(chars.slice(index, index + maxCharsPerLine).join(""));
  }
  return chunks;
}

function wrapTextToLines(text: string, maxCharsPerLine: number) {
  const safeMaxChars = Math.max(1, maxCharsPerLine);
  const lines: string[] = [];
  const paragraphs = String(text || "").split("\n");

  paragraphs.forEach((paragraph, paragraphIndex) => {
    const trimmed = paragraph.replace(/\s+/g, " ").trim();
    if (!trimmed) {
      if (paragraphIndex < paragraphs.length - 1 || !lines.length) {
        lines.push("");
      }
      return;
    }

    const tokens = trimmed.split(" ");
    let current = "";

    tokens.forEach((token) => {
      const pieces = breakLongToken(token, safeMaxChars);
      pieces.forEach((piece) => {
        if (!current) {
          current = piece;
          return;
        }
        const next = `${current} ${piece}`;
        if (charLength(next) <= safeMaxChars) {
          current = next;
          return;
        }
        lines.push(current);
        current = piece;
      });
    });

    if (current) {
      lines.push(current);
    }
  });

  return lines.length ? lines : [""];
}

function ellipsizeLine(value: string, maxCharsPerLine: number) {
  if (charLength(value) <= maxCharsPerLine) return value;
  const chars = Array.from(value);
  const safeLength = Math.max(1, maxCharsPerLine - 1);
  return `${chars.slice(0, safeLength).join("")}\u2026`;
}

function truncateToTextLimit(text: string, textLimit?: number) {
  if (!Number.isFinite(Number(textLimit)) || Number(textLimit) < 0) {
    return { text, truncated: false };
  }
  const limit = Math.max(0, Math.round(Number(textLimit)));
  let visibleCount = 0;
  let truncated = false;
  const chars: string[] = [];
  for (const char of Array.from(text)) {
    if (char !== "\n") {
      if (visibleCount >= limit) {
        truncated = true;
        break;
      }
      visibleCount += 1;
    }
    chars.push(char);
  }
  return {
    text: chars.join(""),
    truncated,
  };
}

function fitText({
  text,
  fontKey,
  requestedFontSize = DEFAULT_FONT_SIZE,
  areaWidthPx,
  areaHeightPx,
  maxLines,
  textLimit,
}: {
  text: string;
  fontKey?: string;
  requestedFontSize?: number;
  areaWidthPx: number;
  areaHeightPx: number;
  maxLines?: number;
  textLimit?: number;
}) {
  const font = getFontConfig(fontKey);
  const limited = truncateToTextLimit(String(text || "").replace(/\r\n?/g, "\n"), textLimit);
  const sanitizedText = limited.text;
  const safeMaxLines = Math.max(1, Math.round(maxLines || 2));
  const safeWidth = Math.max(24, areaWidthPx);
  const safeHeight = Math.max(24, areaHeightPx);
  const minimumFontSize = 8;
  const requestedFloor = Math.max(
    minimumFontSize,
    Math.round(
      Number.isFinite(Number(requestedFontSize))
        ? Number(requestedFontSize)
        : DEFAULT_FONT_SIZE,
    ),
  );
  const heightDrivenSize = Math.floor(safeHeight / Math.max(0.75, font.lineHeight));
  const startFontSize = Math.max(
    requestedFloor,
    Math.min(260, Math.max(minimumFontSize, heightDrivenSize)),
  );

  for (
    let fontSize = startFontSize;
    fontSize >= minimumFontSize;
    fontSize -= 1
  ) {
    const maxCharsPerLine = Math.max(
      1,
      Math.floor(safeWidth / (fontSize * font.widthFactor))
    );
    const lines = wrapTextToLines(sanitizedText, maxCharsPerLine);
    const height = lines.length * fontSize * font.lineHeight;
    if (lines.length <= safeMaxLines && height <= safeHeight) {
      return {
        sanitizedText,
        lines,
        fittedFontSize: fontSize,
        lineHeight: font.lineHeight,
        letterSpacingPx: font.letterSpacingPx,
      };
    }
  }

  const fallbackFontSize = minimumFontSize;
  const fallbackMaxChars = Math.max(
    1,
    Math.floor(safeWidth / (fallbackFontSize * font.widthFactor))
  );
  const wrapped = wrapTextToLines(sanitizedText, fallbackMaxChars);
  const limitedLines = wrapped.slice(0, safeMaxLines);
  const lastLineIndex = limitedLines.length - 1;
  if (wrapped.length > safeMaxLines && lastLineIndex >= 0) {
    limitedLines[lastLineIndex] = ellipsizeLine(
      limitedLines[lastLineIndex],
      fallbackMaxChars
    );
  }

  return {
    sanitizedText,
    lines: limitedLines,
    fittedFontSize: fallbackFontSize,
    lineHeight: font.lineHeight,
    letterSpacingPx: font.letterSpacingPx,
  };
}

function useElementSize<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (!ref.current || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const nextWidth = Math.round(entry.contentRect.width);
      const nextHeight = Math.round(entry.contentRect.height);
      setSize((prev) =>
        prev.width === nextWidth && prev.height === nextHeight
          ? prev
          : { width: nextWidth, height: nextHeight }
      );
    });
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return { ref, size };
}

function EngravingFace({
  label,
  image,
  side,
  data,
  onLayout,
}: {
  label?: string;
  image?: string;
  side: "front" | "back";
  data: EngravingPreviewSide;
  onLayout?: (layout: EngravingPreviewLayout) => void;
}) {
  const { ref, size } = useElementSize<HTMLDivElement>();
  const [imageNaturalSize, setImageNaturalSize] = useState<{
    width: number;
    height: number;
  } | null>(null);

  useEffect(() => {
    setImageNaturalSize(null);
  }, [image]);

  const imageBounds = useMemo(
    () =>
      getContainedImageBounds(
        size.width,
        size.height,
        imageNaturalSize?.width,
        imageNaturalSize?.height
      ),
    [imageNaturalSize?.height, imageNaturalSize?.width, size.height, size.width]
  );
  const displayArea = useMemo(
    () => resolveDisplayArea(data.area, imageBounds),
    [data.area, imageBounds]
  );
  const fallbackLayout = useMemo(() => {
    const area = normalizeArea(data.area);
    const referenceWidthPx =
      Number(data.referenceWidthPx) ||
      imageNaturalSize?.width ||
      LAYOUT_REFERENCE_BOX_PX;
    const referenceHeightPx =
      Number(data.referenceHeightPx) ||
      imageNaturalSize?.height ||
      LAYOUT_REFERENCE_BOX_PX;
    const contentPaddingPct = Number(data.contentPaddingPct ?? CONTENT_PADDING_PCT);
    const contentPaddingRatio = Math.max(0, 1 - (contentPaddingPct * 2) / 100);
    return fitText({
      text: data.sanitizedText || data.text || "",
      fontKey: data.fontKey,
      requestedFontSize: data.fontSize,
      areaWidthPx:
        ((referenceWidthPx * Number(area.widthPct || 70)) / 100) *
        contentPaddingRatio,
      areaHeightPx:
        ((referenceHeightPx * Number(area.heightPct || 22)) / 100) *
        contentPaddingRatio,
      maxLines: data.maxLines ?? data.area?.maxLines ?? area.maxLines,
      textLimit: data.textLimit ?? data.area?.textLimit ?? area.textLimit,
    });
  }, [
    data.area,
    data.contentPaddingPct,
    data.fontKey,
    data.fontSize,
    data.maxLines,
    data.referenceHeightPx,
    data.referenceWidthPx,
    data.sanitizedText,
    data.text,
    data.textLimit,
    imageNaturalSize?.height,
    imageNaturalSize?.width,
  ]);

  const referenceWidth =
    Number(data.referenceWidthPx) ||
    imageNaturalSize?.width ||
    LAYOUT_REFERENCE_BOX_PX;
  const referenceHeight =
    Number(data.referenceHeightPx) ||
    imageNaturalSize?.height ||
    LAYOUT_REFERENCE_BOX_PX;
  const lines =
    Array.isArray(data.lines) && data.lines.length ? data.lines : fallbackLayout.lines;
  const lineHeight = Number(data.lineHeight || fallbackLayout.lineHeight || 1.12);
  const fittedFontSize = Number(
    data.fittedFontSize ||
      fallbackLayout.fittedFontSize ||
      data.fontSize ||
      DEFAULT_FONT_SIZE,
  );
  const contentOffsetXPct = clamp(
    Number(data.contentOffsetXPct ?? 0),
    -MAX_CONTENT_OFFSET_PCT,
    MAX_CONTENT_OFFSET_PCT
  );
  const contentOffsetYPct = clamp(
    Number(data.contentOffsetYPct ?? 0),
    -MAX_CONTENT_OFFSET_PCT,
    MAX_CONTENT_OFFSET_PCT
  );
  const contentScale = clamp(
    Number(data.contentScale ?? 1) || 1,
    MIN_CONTENT_SCALE,
    MAX_CONTENT_SCALE
  );
  const contentPaddingPct = Number(data.contentPaddingPct ?? CONTENT_PADDING_PCT);
  const contentPaddingRatio = Math.max(0, 1 - (contentPaddingPct * 2) / 100);
  const renderedContentWidthPx =
    ((size.width || referenceWidth) * Number(displayArea.widthPct || 0)) / 100 *
    contentPaddingRatio;
  const renderedContentHeightPx =
    ((size.height || referenceHeight) * Number(displayArea.heightPct || 0)) / 100 *
    contentPaddingRatio;
  const areaScale = Math.max(
    0.001,
    Math.min(
      renderedContentWidthPx /
        Math.max(
          1,
          ((referenceWidth * Number(data.area?.widthPct || 70)) / 100) *
            contentPaddingRatio
        ),
      renderedContentHeightPx /
        Math.max(
          1,
          ((referenceHeight * Number(data.area?.heightPct || 22)) / 100) *
            contentPaddingRatio
        )
    )
  );
  const fontSize = Math.max(
    1,
    Number.isFinite(Number(data.normalizedFontSizePct))
      ? (renderedContentHeightPx * Number(data.normalizedFontSizePct)) / 100
      : Number(
          data.fittedFontSize ||
            fallbackLayout.fittedFontSize ||
            data.fontSize ||
            DEFAULT_FONT_SIZE
        ) * areaScale
  );
  const letterSpacing = Number.isFinite(Number(data.normalizedLetterSpacingEm))
    ? fontSize * Number(data.normalizedLetterSpacingEm)
    : Number(data.letterSpacingPx ?? fallbackLayout.letterSpacingPx ?? 0.1) *
      areaScale;

  useEffect(() => {
    if (!onLayout) return;
    onLayout({
      side,
      renderedFontSizePx: Number(fontSize.toFixed(4)),
      fittedFontSize: Number(fittedFontSize.toFixed(4)),
      lineCount: lines.length,
      contentWidthPx: Number(renderedContentWidthPx.toFixed(4)),
      contentHeightPx: Number(renderedContentHeightPx.toFixed(4)),
    });
  }, [
    fittedFontSize,
    fontSize,
    lines.length,
    onLayout,
    renderedContentHeightPx,
    renderedContentWidthPx,
    side,
  ]);

  return (
    <div style={{ display: "grid", gap: 4 }}>
      {label ? (
        <div
          style={{
            fontSize: 11,
            color: "#64748b",
            textTransform: "capitalize",
            fontWeight: 600,
          }}
        >
          {label}
        </div>
      ) : null}
      <div
        ref={ref}
        style={{
          position: "relative",
          width: "100%",
          aspectRatio: "1 / 1",
          border: "1px solid #d1d5db",
          borderRadius: 12,
          overflow: "hidden",
          background:
            "linear-gradient(145deg, rgba(248,250,252,0.95), rgba(226,232,240,0.7))",
        }}
      >
        {image ? (
          <img
            src={assetUrl(image) || image}
            alt={`Engraving ${side} preview`}
            onLoad={(event) => {
              const target = event.currentTarget;
              if (!target.naturalWidth || !target.naturalHeight) return;
              setImageNaturalSize({
                width: target.naturalWidth,
                height: target.naturalHeight,
              });
            }}
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain",
            }}
          />
        ) : (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "grid",
              placeItems: "center",
              color: "#94a3b8",
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            No {side} image
          </div>
        )}

        {data.enabled !== false && lines.some((line) => String(line || "").trim()) ? (
          <div
            style={{
              position: "absolute",
              left: `${displayArea.xPct}%`,
              top: `${displayArea.yPct}%`,
              width: `${displayArea.widthPct}%`,
              height: `${displayArea.heightPct}%`,
              transform: `rotate(${Number(data.area?.rotation || 0)}deg)`,
              transformOrigin: "center center",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: `${contentPaddingPct}%`,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                position: "absolute",
                left: `${50 + contentOffsetXPct}%`,
                top: `${50 + contentOffsetYPct}%`,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                color: FIXED_TEXT_COLOR,
                fontFamily: data.fontFamily || getFontConfig(data.fontKey).family,
                fontWeight:
                  side === "back" && String(data.fontKey || "").toLowerCase() === "script"
                    ? 500
                    : 600,
                fontSize,
                lineHeight,
                letterSpacing,
                textAlign: "center",
                textShadow: "0 0 1px rgba(255,255,255,0.3)",
                mixBlendMode: "multiply",
                transform: `translate(-50%, -50%) scale(${contentScale})`,
                transformOrigin: "center center",
              }}
            >
              {lines.map((line, index) => (
                <span
                  key={`${side}-${index}-${line}`}
                  style={{ display: "block", whiteSpace: "nowrap" }}
                >
                  {line || "\u00A0"}
                </span>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default function EngravingCustomizationPreview({
  selection,
  mode = "auto",
  activeSide,
  showSideLabels = false,
  onSideLayout,
}: Props) {
  if (!selection) return null;
  const frontImage = selection.preview?.frontImage;
  const backImage = selection.preview?.backImage;
  const effectiveSide =
    activeSide ||
    selection.activeSide ||
    (selection.back?.enabled && backImage ? "back" : "front");
  const hasBackPreview =
    !!backImage &&
    !!selection.back &&
    (mode === "split" ||
      (mode === "auto" &&
        selection.back.enabled &&
        !!String(selection.back.sanitizedText || selection.back.text || "").trim()) ||
      effectiveSide === "back");

  if (mode === "split" || (mode === "auto" && hasBackPreview && frontImage)) {
    return (
      <div
        style={{
          display: "grid",
          gap: 8,
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        }}
      >
        <EngravingFace
          side="front"
          image={frontImage}
          data={selection.front}
          label={showSideLabels ? "front" : undefined}
          onLayout={onSideLayout}
        />
        {selection.back ? (
          <EngravingFace
            side="back"
            image={backImage}
            data={selection.back}
            label={showSideLabels ? "back" : undefined}
            onLayout={onSideLayout}
          />
        ) : null}
      </div>
    );
  }

  const faceSide =
    effectiveSide === "back" && selection.back && backImage ? "back" : "front";

  return (
    <EngravingFace
      side={faceSide}
      image={faceSide === "back" ? backImage : frontImage}
      data={faceSide === "back" && selection.back ? selection.back : selection.front}
      label={showSideLabels ? faceSide : undefined}
      onLayout={onSideLayout}
    />
  );
}
